import React, { HTMLAttributes } from "react";
import { cls } from "cls";

export const colors = {
  base: cls(
    "bg-gray-50",
    "hover:bg-gray-100",
    "active:bg-gray-100",
    "disabled:bg-gray-100",
    "text-gray-800",
    "border-black-20",
    "shadow-inset-white-25",
  ),
  light: cls(
    "bg-white",
    "border-gray-600",
    "text-gray-800",
    "hover:bg-gray-primary",
    "focus-visible:outline-gray-800",
    "btn",
  ),
  green: cls(
    "hover:bg-green-800",
    "text-white",
    "bg-green-500",
    "border-black-10",
    "focus-visible:outline-green-800",
    "btn",
  ),
  blue: cls("text-white", "border-black-10", "btn", "btn-info"),
  purple: cls("text-white", "border-black-10", "btn", "btn-custom"),
  glass: cls(
    "bg-white",
    "hover:bg-white",
    "px-2",
    "text-gray-800",
    "capitalize",
    "border-gray-glass",
  ),
  inverse: cls(
    "text-white",
    "bg-gray-inverse",
    "border-black-10",
    "hover:bg-gray-inverse-dark",
  ),
  custom: "",
  red: cls("text-white", "border-black-10", "btn", "btn-cart"),
  "red-outline": cls(
    "bg-transparent",
    "hover:bg-red-800",
    "border-red-800",
    "text-red-700",
    "hover:text-white",
  ),
  "green-solid": cls("text-white", "hover:text-white", "btn", "btn-primary"),
  "green-outline": cls(
    "bg-transparent",
    "hover:bg-green-500",
    "border-green-500",
    "text-green-500",
    "hover:text-white",
  ),
  "glass-outline": cls(
    "bg-transparent",
    "hover:bg-gray-50",
    "border-gray-600",
    "text-gray-800",
    "btn-glass-shadow",
  ),
  ui: cls(
    "text-shadow-white-75",
    "hover:bg-gray-50",
    "border-gray-400",
    "text-gray-800",
    "focus-visible:outline-gray-800",
    "active:shadow-inset-black-15",
  ),
  orange: cls("text-white", "border-black-20", "btn", "btn-checkout"),
};

export const sizes = {
  small: cls(
    "rounded-sm-1/2",
    "text-sm-1/2",
    "leading-3",
    "px-3",
    "pb-1-1/2",
    "pt-1-3/4",
  ),
  standard: cls(
    "rounded-normal",
    "text-base",
    "leading-normal",
    "px-7",
    "py-2-1/2",
  ),
  mini: cls("rounded", "text-xs", "leading-3", "py-1", "px-2"),
  large: cls("rounded", "text-xl", "py-4", "px-10"),
  toolbar: cls("rounded", "text-xs", "leading-3", "px-3", "py-1-1/2"),
  custom: "",
};

type BaseProps = {
  className?: string;
  children: React.ReactNode;
  color: keyof typeof colors;
  size?: keyof typeof sizes;
  alignment?: string;
  weight?: string;
  focusClasses?: string;
};

type AnchorProps = BaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "type"> & {
    href: Exclude<
      React.AnchorHTMLAttributes<HTMLAnchorElement>["href"],
      undefined
    >;
  };

type ButtonProps = BaseProps &
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > & {
    type: Exclude<
      React.ButtonHTMLAttributes<HTMLButtonElement>["type"],
      undefined
    >;
  };

type ElementProps = BaseProps & {
  element: React.ElementType;
} & HTMLAttributes<HTMLElement>;

function Buttons(
  props: ButtonProps,
  ref?: React.RefObject<HTMLButtonElement>,
): JSX.Element;
function Buttons(props: AnchorProps): JSX.Element;
function Buttons(props: ElementProps): JSX.Element;

function Buttons(
  props: ButtonProps | AnchorProps | ElementProps,
  ref?: React.RefObject<HTMLButtonElement>,
) {
  const {
    children,
    className = "",
    size = "standard",
    color,
    alignment = "align-middle",
    weight = "font-semibold",
    focusClasses = cls(
      "focus-visible:outline",
      "focus-visible:outline-offset-2",
      "focus-visible:outline-2",
    ),
    ...rest
  } = props;

  const baseClasses = cls(
    "border-solid",
    "border",
    "box-border",
    "cursor-pointer",
    "inline-block",
    "text-center",
    "no-underline",
    "antialiased",
    focusClasses,
  );
  const colorClass = colors[color];

  const sizeClass = sizes[size];

  const classes = `${baseClasses} ${className} ${sizeClass} ${colorClass} ${alignment} ${weight}`;

  if ("element" in rest) {
    const { element, ...forwardedProps } = rest;

    return React.createElement(
      element,
      {
        ...forwardedProps,
        className: classes,
      },
      children,
    );
  }

  if ("href" in rest) {
    return (
      <a {...rest} className={classes}>
        {children}
      </a>
    );
  }

  if ("type" in rest) {
    return (
      <button ref={ref} {...rest} className={classes}>
        {children}
      </button>
    );
  }
}

export default React.forwardRef(Buttons) as unknown as typeof Buttons;
